import { useState } from 'react'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { useNavigate, useLocation } from 'react-router-dom'
import boatBackground from '../../assets/images/boats/boat_background.png'
import stSeat from '../../assets/images/icons/st_seat.svg'
import vipSeat from '../../assets/images/icons/vip_seat.svg'
import noTripFound from '../../assets/images/icons/no-trip-found.svg'
import boatTrip from '../../assets/images/icons/boat_trip.svg'
import { t } from 'i18next'
import { BoatBookingImage } from 'components/icons'
import { BookingSearchComponent } from 'pages/boatBooking/components/BookingSearchComponent'
import { GET_SEARCH_TRIP, GET_SEARCH_RETURN_TRIP } from 'graphql/common/queries'
import { useLazyQuery } from '@apollo/client'
import loadingImage from '../../assets/images/boat-loading.gif'
import { customTimeFormat, getTimeDifference, utcDateTimeToBahrainTime } from 'utils/common'

function formatDuration(minutes) {
    if (!minutes) return ''
    
    if (minutes < 60) {
        return `${minutes} ${minutes === 1 ? t('minute') : t('minutes')}`
    }
    
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    
    if (remainingMinutes === 0) {
        return `${hours} ${hours === 1 ? t('hour') : t('hours')}`
    }
    
    return `${hours}${t('hr')} ${remainingMinutes}${t('mins')}`
}

function BoatBooking() {
    const navigate = useNavigate()
    const location = useLocation()
    const { filterObj } = location.state || {}
    const [tripList, setTripList] = useState(null)
    const [returnTripList, setReturnTripList] = useState(null)
    const [routeCount, setRouteCount] = useState(0)
    const [searchFilterObj, setSearchFilterObj] = useState({
        fromLocationId: 0,
        toLocationId: 0,
        searchDate: new Date(),
    })
    const [selectedPerson, setSelectedPerson] = useState({
        ADL: 1,
        CHD: 0,
        INF: 0,
        SNR: 0,
    })

    // State for departure and return trips
    const [selectedDepartureTrip, setSelectedDepartureTrip] = useState(null)
    const [selectedReturnTrip, setSelectedReturnTrip] = useState(null)
    const [showReturnTrips, setShowReturnTrips] = useState(false)
    const [currentView, setCurrentView] = useState('departure') // 'departure' or 'return'

    // pagination
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const totalPages = Math.ceil(routeCount / rowsPerPage)

    const [getSearchTripList, { loading: searchListLoading, error: searchListError }] = useLazyQuery(
        GET_SEARCH_TRIP,
        {
            context: { unauth: true },
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.searchTrip.success && data.searchTrip.code === '200') {
                    const allTrips = data.searchTrip.trips
                    // For departure trips, filter by location direction
                    const departureTrips = allTrips.filter(trip => 
                        trip.start?.location_id === searchFilterObj.fromLocationId &&
                        trip.end?.location_id === searchFilterObj.toLocationId
                    )
                    
                    setTripList(departureTrips)
                    setRouteCount(departureTrips?.length)
                    
                    // For one-way trips, clear return trips
                    if (searchFilterObj.tripType !== 'round-trip') {
                        setReturnTripList([])
                    }
                }
            },
        }
    )

    const [getSearchReturnTripList, { loading: returnTripLoading, error: returnTripError }] = useLazyQuery(
        GET_SEARCH_RETURN_TRIP,
        {
            context: { unauth: true },
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                if (data.searchTrip.success && data.searchTrip.code === '200') {
                    const returnTrips = data.searchTrip.trips
                    setReturnTripList(returnTrips)
                }
            },
        }
    )

    // Search Route  List
    const searchTrips = location => {
        // Update selected person count from search
        setSelectedPerson(location.passengers)
        setSearchFilterObj({
            fromLocationId: location.fromLocationId,
            toLocationId: location.toLocationId,
            searchDate: location.searchDate,
            returnDate: location.returnDate,
            tripType: location.tripType,
            routeType: location.routeType,
        })
        
        // Reset selections when starting new search
        setTripList(null)
        setReturnTripList(null)
        setSelectedDepartureTrip(null)
        setSelectedReturnTrip(null)
        setShowReturnTrips(false)
        setCurrentView('departure')
        
        // Prepare search filter for departure trips only
        const searchFilter = [
            {
                from_location_id: location.fromLocationId,
                to_location_id: location.toLocationId,
                trip_type: 'DEPART',
                trip_date: location.searchDate,
            }
        ]
        
        // First call: Get departure trips
        getSearchTripList({
            variables: {
                searchfilter: searchFilter,
                prefered_current_type: 'BHD', // Default to BHD currency
                bookingTripType: location.tripType === 'one-way' ? 'ONE_WAY' : 'ROUND_TRIP',
            },
        })
    }

    const calculateAvailableSeatsCount = tripInfoSeats => {
        let availableSeats = 0
        tripInfoSeats.map(seat => {
            availableSeats += seat.available
        })

        return availableSeats
    }

    const handleDepartureBooking = tripInfo => {
        setSelectedDepartureTrip(tripInfo)
        
        if (searchFilterObj.tripType === 'round-trip') {
            // Make second API call for return trips with departure trip details
            const returnSearchFilter = [
                {
                    from_location_id: searchFilterObj.fromLocationId, // Return trip starts from destination
                    to_location_id: searchFilterObj.toLocationId, // Return trip ends at origin
                    trip_type: 'RETURN',
                    trip_date: searchFilterObj.returnDate,
                }
            ]
            
            const previousSelectedTripDetails = {
                to_location_id: searchFilterObj.toLocationId,
                trip_date: searchFilterObj.searchDate,
                trip_id: tripInfo.trip_id
            }
            
            getSearchReturnTripList({
                variables: {
                    searchfilter: returnSearchFilter,
                    prefered_current_type: 'BHD',
                    previous_selected_trip_details: previousSelectedTripDetails,
                    bookingTripType: 'ROUND_TRIP',
                },
            })
            
            // Show return trips for round trip
            setShowReturnTrips(true)
            setCurrentView('return')
        } else {
            // Direct to booking details for one-way trip
            const stateObj = {
                tripDetails: tripInfo,
                filterObj: {
                    person: selectedPerson,
                    form: searchFilterObj,
                },
            }
            navigate('/boat-booking/detail', {
                state: stateObj,
            })
            window.scrollTo(0, 0)
        }
    }

    const handleReturnBooking = tripInfo => {
        setSelectedReturnTrip(tripInfo)
        
        // Navigate to booking details with both trips
        const stateObj = {
            tripDetails: tripInfo, // This will be the return trip
            departureTrip: selectedDepartureTrip, // Store departure trip separately
            filterObj: {
                person: selectedPerson,
                form: searchFilterObj,
            },
            isRoundTrip: true,
        }
        navigate('/boat-booking/detail', {
            state: stateObj,
        })
        window.scrollTo(0, 0)
    }

    const handleClearTripList = () => {
        setTripList(null)
        setReturnTripList(null)
        setSelectedDepartureTrip(null)
        setSelectedReturnTrip(null)
        setShowReturnTrips(false)
        setCurrentView('departure')
    }

    const handleBackToDeparture = () => {
        setCurrentView('departure')
        setShowReturnTrips(false)
        setSelectedReturnTrip(null)
        // Don't clear returnTripList to preserve return trips data when navigating back
    }

    // Filter trips based on current view
    const getFilteredTrips = () => {
        if (currentView === 'departure') {
            return tripList || []
        } else {
         
            return returnTripList || []
        }
    }

    const filteredTrips = getFilteredTrips()

    return (
        <>
            <></>
            <div>
                <div className="relative">
                    <div className="rounded-[15px] h-[250px] md:h-[180px] overflow-hidden">
                        <img src={boatBackground} alt="Background " className="w-full h-full object-cover" />
                    </div>

                    {/* Overlay Container */}
                    <div className="flex flex-col h-full p-4 md:px-4 mt-[-220px] md:mt-[-150px]">
                        {/* Title */}
                        <h2 className="text-white text-xl sm:text-2xl font-bold mb-4 sm:mb-5 text-center">{t('Book your Boat')}</h2>

                        {/* Booking Card */}
                        <div className="bg-white p-4 sm:p-3 md:p-6 pb-0 rounded-xl shadow-lg w-full">
                            {/* First Row - Person Count & Seat Selection */}
                            <BookingSearchComponent
                                selectedSearchFilter={searchFilterObj}
                                selectedPerson={selectedPerson}
                                onSearch={searchFilter => searchTrips(searchFilter)}
                                onClear={handleClearTripList}
                            />
                        </div>
                    </div>
                </div>
                <div>
                    {/* Header with navigation for round trips */}
                    <div className="flex flex-col md:flex-row justify-between border-b p-4 md:p-3 w-full mb-6 mt-4">
                        <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4 mb-3 md:mb-0">
                            <p className="font-lg">
                                {currentView === 'departure' ? t('Departure Trips') : t('Return Trips')}
                            </p>
                            {showReturnTrips && currentView === 'return' && (
                                <button
                                    onClick={handleBackToDeparture}
                                    className="text-sm text-primary hover:text-primary/80 underline"
                                >
                                    ← {t('Back to Departure')}
                                </button>
                            )}
                        </div>
                        
                        {/* Show selected departure info for return trips */}
                        {currentView === 'return' && selectedDepartureTrip && (
                            <div className="text-sm text-gray-600">
                                <span className="font-medium">{t('Departure')}:</span> {selectedDepartureTrip.trip?.trip_name?.en} - {selectedDepartureTrip.trip_date}
                            </div>
                        )}
                    </div>

                    {(searchListLoading || returnTripLoading) && (
                        <div className="flex flex-col justify-center items-center bg-white h-96 mt-16">
                            <img alt="logo" src={loadingImage} className="w-[250px]" />
                            <p className="pt-3 text-primary font-semibold">Loading, please wait...</p>
                        </div>
                    )}
                    
                    {!searchListLoading && !returnTripLoading && !filteredTrips && (
                        <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                            <img src={boatTrip} className="w-25 h-25 m-auto" />
                            <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('Book Your Boat')}</h3>
                            <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                            <div className="mt-6"></div>
                        </div>
                    )}
                    
                    {!searchListLoading && !returnTripLoading && filteredTrips?.length === 0 && (
                        <div className="text-center py-24 bg-gray-50 mt-8 rounded-lg">
                            <img src={noTripFound} className="w-25 h-25 m-auto" />
                            <h3 className="mt-2 text-sm font-semibold text-gray-900">
                                {currentView === 'departure' ? t('No Departure Trips Found!') : t('No Return Trips Found!')}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">{t('Try adjusting your search criteria')}</p>
                            <div className="mt-6"></div>
                        </div>
                    )}
                    
                    {!searchListLoading && !returnTripLoading && !searchListError && !returnTripError && filteredTrips?.length > 0 && (
                        <div>
                            {filteredTrips.map(tripInfo => (
                                <div
                                    key={`${tripInfo.trip_id}-${tripInfo.trip_date}`}
                                    className="divide-y divide-gray-200 mb-4 overflow-hidden rounded-lg bg-white shadow-lg border-solid border-2 border-gray-200"
                                >
                                    <div className="px-4 py-5 sm:p-6">
                                        <div className="flex flex-col md:flex-row justify-between">
                                            <div className="flex w-full md:w-2/6 mb-6 md:mb-0">
                                                <BoatBookingImage className="w-[50px] h-[50px] fill-[#285f74]" />
                                                <div className="ml-4">
                                                    <h4 className="text-lg font-bold text-gray-900 pb-1">
                                                        {tripInfo.trip?.trip_name?.en}
                                                    </h4>
                                                    <span className="inline-flex items-center gap-x-1.5 rounded-full bg-primary px-2 py-1 text-xs font-medium text-white">
                                                        <InformationCircleIcon
                                                            aria-hidden="true"
                                                            className="h-4 w-4 text-white font-[12px]"
                                                        />

                                                        {tripInfo.route?.route_name?.en}
                                                    </span>
                                                    {tripInfo.trip_date && (
                                                        <div className="mt-1 text-sm text-gray-600">
                                                            {currentView === 'departure' ? t('Departure Date') : t('Return Date')}: {tripInfo.trip_date}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex flex-col md:flex-row md:justify-normal xl:justify-end w-full xl:w-4/6 mt-2">
                                                <div className="place-content-center text-start mb-3 md:mb-0">
                                                    <p className="font-bold font-lg">
                                                        {customTimeFormat(tripInfo.start?.departure_time)}
                                                    </p>
                                                    <p className="font-sm">{tripInfo.start?.location?.name?.en}</p>
                                                </div>
                                                <div className="flex-1 xl:flex xl:grow-0 basis-1/2 place-content-center basis-1/2 ms-0 md:ms-4 me-0 md:me-4 relative">
                                                    <p className="text-gray-500 font-sm text-center pb-3 mb-2 md:mb-0 md:absolute md:right-0 md:left-0 md:top-[-10px]">
                                                        {tripInfo.duration_minutes ? formatDuration(tripInfo.duration_minutes) : getTimeDifference(
                                                            tripInfo.start?.departure_time,
                                                            tripInfo.end?.arrival_time
                                                        )}{' '}
                                                        | 0 stops
                                                    </p>
                                                    {/* Mobile: Simple trip details */}
                                                    <div className="block md:hidden">
                                                        <div className="text-center space-y-3">
                                                            {/* Trip summary */}
                                                            <div className="bg-gray-50 rounded-lg p-3">
                                                                <div className="text-sm text-gray-600 mb-2">
                                                                    <span className="font-medium">{tripInfo.start?.location?.name?.en}</span>
                                                                    <span className="mx-2 text-primary">→</span>
                                                                    <span className="font-medium">{tripInfo.end?.location?.name?.en}</span>
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {tripInfo.duration_minutes ? formatDuration(tripInfo.duration_minutes) : getTimeDifference(
                                                                        tripInfo.start?.departure_time,
                                                                        tripInfo.end?.arrival_time
                                                                    )} • 0 stops
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {/* Desktop: Simple route visualization */}
                                                    <div className="hidden md:flex items-center w-full">
                                                        <div className="flex items-center w-full">
                                                            <span className="border-primary border w-[15px] h-[15px] rounded-full bg-primary"></span>
                                                            <div className="flex-1 h-1 bg-gray-200 mx-2"></div>
                                                            <span className="border-primary border w-[15px] h-[15px] rounded-full bg-primary"></span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="place-content-center text-end mt-3 md:mt-0">
                                                    <p className="font-bold font-lg">
                                                        {customTimeFormat(tripInfo.end?.arrival_time)}
                                                    </p>
                                                    <p className="font-sm">{tripInfo.end?.location?.name?.en}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="px-4 py-4 sm:px-6 ">
                                        <div className="flex flex-col md:flex-row justify-between">
                                            <div className="flex flex-col md:flex-row justify-start md:justify-normal w-full md:w-3/6 mb-4 md:mb-0">
                                                <div className="flex-none mb-3 md:mb-0">
                                                    {/* <p className="pb-3">Facilities</p>
                                                    <div className="flex flex-row gap-4">
                                                        <WifiIcon aria-hidden="true" className="h-6 w-6 text-primary " />
                                                        <WifiIcon aria-hidden="true" className="h-6 w-6 text-primary " />
                                                        <WifiIcon aria-hidden="true" className="h-6 w-6 text-primary " />
                                                        <WifiIcon aria-hidden="true" className="h-6 w-6 text-primary " />
                                                        <WifiIcon aria-hidden="true" className="h-6 w-6 text-primary " />
                                                    </div> */}
                                                </div>

                                                {calculateAvailableSeatsCount(tripInfo.available_seats) > 0 ? (
                                                    <div className="flex-none">
                                                        <p className="pb-3">{t('Available Seats')}</p>
                                                        <div className="flex flex-col md:flex-row gap-3 md:gap-6">
                                                            {tripInfo.available_seats?.map(seat => (
                                                                <p key={seat.code} className="flex">
                                                                    <img
                                                                        src={seat.code === 'VIP' ? vipSeat : stSeat}
                                                                        className="w-[20px] me-2"
                                                                    />
                                                                    <p className="text-[14px]"> {seat.available} </p>
                                                                </p>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex-none">
                                                        <p className="pb-3">{t('Available Seats')}</p>
                                                        <div className="flex flex-col md:flex-row gap-3 md:gap-6">
                                                            <p className="flex">
                                                                <img src={stSeat} className="w-[20px] me-2" />
                                                                <p className="text-[14px]"> 0 </p>
                                                            </p>
                                                            <p className="flex">
                                                                <img src={vipSeat} className="w-[20px] me-2" />
                                                                <p className="text-[14px]"> 0 </p>
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {calculateAvailableSeatsCount(tripInfo.available_seats) > 0 ? (
                                                <div className="flex w-full md:w-2/6 justify-center md:justify-end place-items-center">
                                                    <button
                                                        className="h-[max-content] w-full md:w-auto hover:bg-primary/[.9] bg-primary focus-visible:outline-primary justify-center rounded-md px-5 py-3 text-md font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                                        onClick={() => 
                                                            currentView === 'departure' 
                                                                ? handleDepartureBooking(tripInfo)
                                                                : handleReturnBooking(tripInfo)
                                                        }
                                                    >
                                                        {currentView === 'departure' ? t('Select Departure') : t('Select Return')}
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex w-full md:w-2/6 justify-center md:justify-end place-items-center">
                                                    <button
                                                        className="disabled cursor-not-allowed h-[max-content] w-full md:w-auto hover:bg-gray/[.9] bg-gray-400  justify-center rounded-md px-5 py-3 text-md font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                                                        onClick={() => { }}
                                                    >
                                                        {t('Sold out')}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default BoatBooking
